import { Component, OnDestroy, OnInit } from "@angular/core";
import { UserInfoService } from "./user-info.service";
import { NavigationEnd, Router } from "@angular/router";
import {
  ClientNotificationService,
  NotificationMessage
} from "./client-notification.service";
import {
  animate,
  keyframes,
  style,
  transition,
  trigger
} from "@angular/animations";
import { ConfigStateService } from "./config/config-state.service";
import { Title } from '@angular/platform-browser';
import { MsalBroadcastService, MsalService } from "@azure/msal-angular";
import { InteractionStatus } from "@azure/msal-browser";
import { Subject } from "rxjs";
import { filter, takeUntil } from "rxjs/operators";

declare const EventSource: any;

@Component({
    selector: "app-root",
    templateUrl: "./app.component.html",
    styleUrls: ["./app.component.scss"],
    animations: [
        trigger("message", [
            transition(":enter", animate(500, keyframes([
                style({ top: "-200px", opacity: 0 }),
                style({ top: "0", opacity: 1 })
            ]))),
            transition(":leave", animate(500, keyframes([
                style({ top: "0", opacity: 1 }),
                style({ top: "-200px", opacity: 0 })
            ])))
        ])
    ],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = "KPMG Expat Tax Solution";
  hideSidebar: RegExpMatchArray; // the right sidebar
  serverMessages: NotificationMessage[] = [];
  noframe: Boolean = window.location === window.parent.location;
  private readonly destroying$ = new Subject<void>();

  isEss: boolean = false;
  constructor(
    public userInfo: UserInfoService,
    private router: Router,
    private clientNotificationService: ClientNotificationService,
    private configStateService: ConfigStateService,
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
    private titleService: Title,
  ) {}

  // hide right sidebar when on calculation plan page
  // need to use regex to check url as calc plan is a child route and router only returns parent route
  ngOnInit() {
    this.msalService.handleRedirectObservable().subscribe();
    this.configStateService.loadConfig();
    if (window.location.pathname.startsWith("/ess")) {
      this.isEss = true;
      this.setTitle("ESS Assist");
      this.setIcon("image/x-icon", "assets/favicon-purple.ico");
    }

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd)
        this.hideSidebar = event.url.match(/\/settings\/calculations\//);
    });
    this.clientNotificationService.$newMessage.subscribe(
      (msg: NotificationMessage) => this.serverMessages.unshift(msg)
    );
    // Wait for interaction status to equal 'none' to account for periods where local storage
    // values used for the `isLoggedIn` check and the msal internal state are being reconciled
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
        if (!this.userInfo.subscribeRunning && this.userInfo.isLoggedIn()) {
          this.userInfo.subscribeRunning = true;
          this.clientNotificationService.connect(this.userInfo);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  dismissServerMessage(index: number) {
    this.serverMessages.splice(index, 1);
  }
  dismissAllMessages() {
    this.serverMessages = [];
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle( newTitle );
  }

  setIcon(type: string, href: string) {
    const linkElement = document.querySelector("link[rel*='icon']") || document.createElement('link');
    linkElement.setAttribute( "rel", "short icon" );
    linkElement.setAttribute( "type", type );
    linkElement.setAttribute( "href", href );
    document.head.appendChild( linkElement );
  }

  isArray(input: unknown): input is Array<unknown> {
    return Array.isArray(input);
  }
}
